import { abyssColors } from '@/theme/colors';
import { Edit, RestartAlt } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { selectedPOIType } from '../../state';
import { FullWidthPanel } from './styles';

const BLISTER_FIELDS = [
  {
    key: 'estimatedHeight',
    defaultName: 'Estimated Height',
    description: 'Height calculated by the blister detection tool',
  },
  {
    key: 'manualSetHeight',
    defaultName: 'Manual Set Height',
    description: 'Manually set height value',
  },
  {
    key: 'blisterHeightIndex',
    defaultName: 'Blister Height Index',
    description: 'Index value for blister height',
  },
  {
    key: 'maxHeight',
    defaultName: 'Blister Height',
    description: 'Maximum height of the blister',
  },
  {
    key: 'wallLoss',
    defaultName: 'Estimated Wall Loss',
    description: 'Estimated wall loss measurement',
  },
  {
    key: 'residualThickness',
    defaultName: 'Residual Thickness',
    description: 'Remaining wall thickness',
  },
  {
    key: 'residualCorrosionAllowance',
    defaultName: 'Residual Corrosion Allowance',
    description: 'Remaining corrosion allowance',
  },
  {
    key: 'pdrHeight',
    defaultName: 'PDR Height',
    description: 'Paint Damage Report height measurement',
  },
  {
    key: 'heightType',
    defaultName: 'Calculation Method',
    description:
      'Method used to calculate height (e.g., PDR Tool, Blister Tool, Manual, Uncertain)',
  },
  {
    key: 'verifiedMaxHeight',
    defaultName: 'Verified Height',
    description: 'Inspector verified maximum height',
  },
  {
    key: 'verifiedResidualThickness',
    defaultName: 'Verified Residual Thickness',
    description: 'Inspector verified residual thickness',
  },
  {
    key: 'verifiedResidualCorrosionAllowance',
    defaultName: 'Verified Residual Corrosion Allowance',
    description: 'Inspector verified residual corrosion allowance',
  },
  {
    key: 'distanceConfidence',
    defaultName: 'Distance Confidence',
    description: 'Confidence level for distance measurement',
  },
  {
    key: 'measurementConfidence',
    defaultName: 'Measurement Confidence',
    description: 'Confidence level for measurement accuracy',
  },
];

export const PoiBlisterFieldsPanel = ({
  handleSaveTemplate,
}: {
  handleSaveTemplate: () => void;
}) => {
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');

  const handleFieldNameChange = (fieldKey: string, newName: string) => {
    const config = selectedType?.blisterFieldsConfig;
    const currentConfig = config?.[fieldKey as keyof typeof config] as
      | { name?: string | null; hidden?: boolean | null }
      | undefined;

    setSelectedType({
      ...selectedType,
      blisterFieldsConfig: {
        ...selectedType?.blisterFieldsConfig,
        [fieldKey]: {
          ...currentConfig,
          name: newName,
        },
      },
    });
  };

  const handleFieldHiddenChange = (fieldKey: string, hidden: boolean) => {
    const config = selectedType?.blisterFieldsConfig;
    const currentConfig = config?.[fieldKey as keyof typeof config] as
      | { name?: string | null; hidden?: boolean | null }
      | undefined;

    setSelectedType({
      ...selectedType,
      blisterFieldsConfig: {
        ...selectedType?.blisterFieldsConfig,
        [fieldKey]: {
          ...currentConfig,
          hidden,
        },
      },
    });
  };

  const handleResetField = (fieldKey: string) => {
    const updatedConfig = { ...selectedType?.blisterFieldsConfig };
    delete updatedConfig[fieldKey as keyof typeof updatedConfig];

    setSelectedType({
      ...selectedType,
      blisterFieldsConfig: updatedConfig,
    });
  };

  const getFieldValue = (fieldKey: string, defaultName: string): string => {
    const config = selectedType?.blisterFieldsConfig;
    return (
      (config?.[fieldKey as keyof typeof config] as { name?: string | null })?.name ?? defaultName
    );
  };

  const getFieldHidden = (fieldKey: string): boolean => {
    const config = selectedType?.blisterFieldsConfig;
    return (
      (config?.[fieldKey as keyof typeof config] as { hidden?: boolean | null })?.hidden ?? false
    );
  };

  return (
    <>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          {`Blister Field Configuration for ${selectedType?.name}`}
        </Typography>
        {mode === 'Edit' ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              onClick={() => {
                setMode('ReadOnly');
                handleSaveTemplate();
              }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setMode('ReadOnly');
              }}
            >
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip title="Edit Blister Field Configuration" placement="top" arrow>
              <IconButton size="medium" onClick={() => setMode('Edit')}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <FullWidthPanel>
        <Stack sx={{ flex: 1, gap: '3.2rem', width: '100%' }}>
          <Stack gap={2}>
            <Typography variant="h4">Customize Blister Field Configuration</Typography>
            <Typography variant="body2" color="text.secondary">
              Customize the display names and visibility for blister-related fields. These settings
              will be used throughout the application in tables, details panels, and reports. Hidden
              fields will not be displayed.
            </Typography>
          </Stack>

          <Stack gap={3} width="100%">
            {BLISTER_FIELDS.map((field) => {
              const currentValue = getFieldValue(field.key, field.defaultName);
              const isHidden = getFieldHidden(field.key);
              const config = selectedType?.blisterFieldsConfig;
              const fieldConfig = config?.[field.key as keyof typeof config] as
                | { name?: string | null; hidden?: boolean | null }
                | undefined;
              const isModified =
                fieldConfig?.name !== undefined || fieldConfig?.hidden !== undefined;

              return (
                <Grid
                  container
                  key={field.key}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    padding: 2,
                    borderRadius: 1,
                    backgroundColor: isModified ? abyssColors.primary[50] : abyssColors.neutral[50],
                    border: `1px solid ${
                      isModified ? abyssColors.primary[200] : abyssColors.neutral[200]
                    }`,
                    opacity: isHidden ? 0.6 : 1,
                  }}
                >
                  <Grid size={3}>
                    <Stack gap={0.5}>
                      <Typography variant="h6" fontWeight={500}>
                        {field.defaultName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {field.description}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={5}>
                    <TextField
                      fullWidth
                      disabled={mode === 'ReadOnly' || isHidden}
                      value={currentValue}
                      onChange={(e) => handleFieldNameChange(field.key, e.target.value)}
                      placeholder={field.defaultName}
                      variant="outlined"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isHidden}
                          onChange={(e) => handleFieldHiddenChange(field.key, e.target.checked)}
                          disabled={mode === 'ReadOnly'}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Hidden
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={mode === 'ReadOnly' || !isModified}
                      startIcon={<RestartAlt />}
                      onClick={() => handleResetField(field.key)}
                      sx={{
                        color: abyssColors.neutral[600],
                        borderColor: abyssColors.neutral[300],
                        '&:hover': {
                          borderColor: abyssColors.neutral[400],
                          backgroundColor: abyssColors.neutral[100],
                        },
                      }}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              );
            })}
          </Stack>

          <Stack
            gap={1}
            padding={2}
            sx={{
              backgroundColor: abyssColors.primary[50],
              borderRadius: 1,
              border: `1px solid ${abyssColors.primary[200]}`,
            }}
          >
            <Typography variant="h6" color={abyssColors.primary[700]}>
              💡 Tip
            </Typography>
            <Typography variant="body2" color={abyssColors.primary[700]}>
              Custom field configurations will be applied to:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '2rem' }}>
              <li>
                <Typography variant="body2" color={abyssColors.primary[700]}>
                  <strong>Custom Names:</strong> Display names in tables, panels, and reports
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color={abyssColors.primary[700]}>
                  <strong>Hidden Fields:</strong> Fields marked as hidden will not be displayed
                  anywhere in the application
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color={abyssColors.primary[700]}>
                  <strong>Applies to:</strong> POI tables, details panels, verification forms,
                  review screens, and reports
                </Typography>
              </li>
            </ul>
          </Stack>
        </Stack>
      </FullWidthPanel>
    </>
  );
};
